import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Key, ArrowRight, Download, Cpu, FileText, FileSpreadsheet, FileJson, Clock } from "lucide-react";
import { getExportUrl } from "@/lib/api";

const PREVIEW_ROWS = 50;

export default function DBViewer({ data, jobId, modelUsed, onSignUp, expiresAt }) {
  const [activeTab, setActiveTab] = useState(0);
  const [expiryText, setExpiryText] = useState("");
  const [expiryColor, setExpiryColor] = useState("var(--sdb-text-muted)");

  useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) {
        setExpiryText("Expired");
        setExpiryColor("#ef4444");
        return;
      }
      const hours = diff / (1000 * 60 * 60);
      if (hours > 24) {
        setExpiryText("Expires in " + Math.floor(hours / 24) + "d");
        setExpiryColor("var(--sdb-text-muted)");
      } else if (hours > 2) {
        setExpiryText("Expires in " + Math.floor(hours) + "h");
        setExpiryColor("#eab308");
      } else {
        setExpiryText("Expires soon!");
        setExpiryColor("#ef4444");
      }
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!data?.tables?.length) return null;

  const table = data.tables[activeTab];
  if (!table) return null;

  const totalRows = data.tables.reduce((s, t) => s + (t.rows?.length || 0), 0);
  const fkCount = table.columns?.filter((c) => c.fk).length || 0;

  return (
    <div data-testid="db-viewer" className="animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span
              className="font-mono text-[10px] tracking-[3px] uppercase"
              style={{ color: "var(--sdb-accent)" }}
            >
              Generated Database
            </span>
            {expiresAt && expiryText && (
              <Badge
                variant="outline"
                className="font-mono text-[9px] rounded-none px-1.5 py-0"
                style={{ borderColor: expiryColor, color: expiryColor }}
              >
                <Clock className="w-2.5 h-2.5 mr-1" />
                {expiryText}
              </Badge>
            )}
          </div>
          <h3 className="text-xl font-semibold mb-1" style={{ color: "var(--sdb-text)" }}>
            {data.database_name || "mock_db"}
          </h3>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px]" style={{ color: "var(--sdb-text-muted)" }}>
              {data.tables.length} tables &middot; {totalRows} rows
            </span>
            {modelUsed && (
              <Badge
                variant="outline"
                className="font-mono text-[9px] rounded-none px-1.5 py-0"
                style={{ borderColor: "var(--sdb-border)", color: "var(--sdb-text-muted)" }}
              >
                <Cpu className="w-2.5 h-2.5 mr-1" />
                {modelUsed}
              </Badge>
            )}
          </div>
        </div>
        {jobId ? (
          <div className="flex gap-2">
            <Button
              data-testid="export-sql-btn"
              onClick={() => window.open(getExportUrl(jobId, "sql"), "_blank")}
              className="font-mono text-[10px] rounded-sm px-3 py-2 h-auto"
              style={{ background: "var(--sdb-surface)", border: "1px solid var(--sdb-border)", color: "var(--sdb-text-secondary)" }}
            >
              <FileText className="w-3 h-3 mr-1" />
              SQL
            </Button>
            <Button
              data-testid="export-csv-btn"
              onClick={() => window.open(getExportUrl(jobId, "csv"), "_blank")}
              className="font-mono text-[10px] rounded-sm px-3 py-2 h-auto"
              style={{ background: "var(--sdb-surface)", border: "1px solid var(--sdb-border)", color: "var(--sdb-text-secondary)" }}
            >
              <FileSpreadsheet className="w-3 h-3 mr-1" />
              CSV
            </Button>
            <Button
              data-testid="export-json-btn"
              onClick={() => window.open(getExportUrl(jobId, "json"), "_blank")}
              className="font-mono text-[10px] rounded-sm px-3 py-2 h-auto"
              style={{ background: "var(--sdb-surface)", border: "1px solid var(--sdb-border)", color: "var(--sdb-text-secondary)" }}
            >
              <FileJson className="w-3 h-3 mr-1" />
              JSON
            </Button>
          </div>
        ) : (
          <Button
            data-testid="download-cta-btn"
            onClick={onSignUp}
            className="gradient-bg text-black font-semibold text-xs rounded-sm px-4 py-2 h-auto glow-btn hover:text-black"
          >
            <Download className="w-3 h-3 mr-1.5" />
            Download Full DB (1000+ rows)
          </Button>
        )}
      </div>

      {/* Table tabs */}
      <div data-testid="table-tabs" className="flex gap-1.5 overflow-x-auto pb-3 mb-4">
        {data.tables.map((tb, i) => (
          <button
            key={i}
            data-testid={`table-tab-${tb.name}`}
            onClick={() => setActiveTab(i)}
            className="px-3.5 py-2 rounded-sm text-xs font-mono whitespace-nowrap transition-colors"
            style={
              i === activeTab
                ? {
                    background: "var(--sdb-accent)",
                    color: "#000",
                    border: "1px solid var(--sdb-accent)",
                    fontWeight: 700,
                  }
                : {
                    background: "var(--sdb-surface)",
                    color: "var(--sdb-text-muted)",
                    border: "1px solid var(--sdb-border)",
                  }
            }
          >
            {tb.name}
          </button>
        ))}
      </div>

      {/* Data table */}
      <div className="data-table-wrap">
        <div
          className="flex justify-between items-center px-4 py-2.5"
          style={{
            background: "var(--sdb-surface)",
            borderBottom: "1px solid var(--sdb-border)",
          }}
        >
          <span className="font-mono text-xs font-semibold" style={{ color: "var(--sdb-accent)" }}>
            {table.name}
          </span>
          <span className="font-mono text-[10px]" style={{ color: "var(--sdb-text-muted)" }}>
            {table.columns?.length || 0} cols &middot; {table.rows?.length || 0} rows
            {fkCount > 0 && ` \u00B7 ${fkCount} FK`}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {(table.columns || []).map((col, i) => (
                  <th key={i}>
                    <div className="flex items-center gap-1">
                      {col.pk && <Key className="w-2.5 h-2.5" style={{ color: "var(--sdb-warn)" }} />}
                      <span style={{ color: "var(--sdb-text-secondary)" }}>{col.name}</span>
                    </div>
                    <div
                      className="text-[9px] mt-0.5 normal-case tracking-normal"
                      style={{ color: "var(--sdb-text-subtle)" }}
                    >
                      {col.type}
                      {col.fk && (
                        <span style={{ color: "var(--sdb-accent2)" }} className="ml-1">
                          <ArrowRight className="w-2 h-2 inline" /> {col.fk}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(table.rows || []).slice(0, PREVIEW_ROWS).map((row, ri) => (
                <tr key={ri}>
                  {(Array.isArray(row) ? row : []).map((cell, ci) => (
                    <td key={ci}>
                      {cell === null || cell === undefined ? (
                        <span style={{ color: "var(--sdb-text-subtle)" }}>NULL</span>
                      ) : (
                        String(cell)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {(table.rows?.length || 0) > PREVIEW_ROWS && (
                <tr>
                  <td
                    colSpan={table.columns?.length || 1}
                    className="text-center font-mono text-[11px] py-3"
                    style={{ color: "var(--sdb-text-subtle)" }}
                  >
                    Showing {PREVIEW_ROWS} of {table.rows.length} rows — download full database above
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-5">
        <p className="font-mono text-[11px] mb-2.5" style={{ color: "var(--sdb-text-subtle)" }}>
          Showing {Math.min(table.rows?.length || 0, PREVIEW_ROWS)} of {table.rows?.length || 0} rows.
          {jobId ? " Download the full database using the export buttons above." : " Sign in to download the full database."}
        </p>
        <Button
          data-testid="signup-cta-btn"
          onClick={onSignUp}
          variant="outline"
          className="font-mono text-xs rounded-sm px-5 py-2 h-auto"
          style={{
            borderColor: "var(--sdb-accent)",
            color: "var(--sdb-accent)",
          }}
        >
          Sign In to Download Full Database
          <ArrowRight className="w-3 h-3 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
